import { useState, useEffect, useRef } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { getCampaignStatus } from 'src/apis/campaign';
import { CampaignStatusResponse } from 'src/types/campaign';

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 24,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      marginBottom: 24,
    },
    statusItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 0',
    },
    statusIcon: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    statusText: {
      flex: 1,
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: 16,
    },
  })
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 500,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

interface ImportStatusModalProps {
  open: boolean;
  campaignId: string;
  onClose: () => void;
}

export const ImportStatusModal = ({
  open,
  campaignId,
  onClose,
}: ImportStatusModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const [status, setStatus] = useState<CampaignStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchStatus = async () => {
    if (!campaignId) return;

    try {
      setLoading(true);
      const response = await getCampaignStatus(campaignId);
      setStatus(response);

      // Stop polling if completed or failed
      if (
        response.processing_status === 'completed' ||
        response.processing_status === 'failed'
      ) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Failed to fetch campaign status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && campaignId) {
      // Fetch immediately
      fetchStatus();

      // Set up polling every 30 seconds
      intervalRef.current = setInterval(() => {
        fetchStatus();
      }, 60000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [open, campaignId]);

  const getStatusIcon = (step: number) => {
    const processingStatus = status?.processing_status;

    if (step === 0) {
      // Pending step
      if (processingStatus === 'pending' || !processingStatus) {
        return <CircularProgress size={20} style={{ color: '#5EA1F2' }} />;
      }
      return <CheckCircleIcon style={{ color: '#17B26A', fontSize: 24 }} />;
    } else if (step === 1) {
      // Processing step
      if (processingStatus === 'processing') {
        return <CircularProgress size={20} style={{ color: '#5EA1F2' }} />;
      } else if (
        processingStatus === 'completed' ||
        processingStatus === 'failed'
      ) {
        return <CheckCircleIcon style={{ color: '#17B26A', fontSize: 24 }} />;
      }
      return null;
    } else {
      // Completed/Failed step
      if (processingStatus === 'completed') {
        return <CheckCircleIcon style={{ color: '#17B26A', fontSize: 24 }} />;
      } else if (processingStatus === 'failed') {
        return <ErrorIcon style={{ color: '#B42318', fontSize: 24 }} />;
      }
      return null;
    }
  };

  const getStatusText = (step: number) => {
    if (step === 0) {
      return 'Pending';
    } else if (step === 1) {
      return 'Processing';
    } else {
      const processingStatus = status?.processing_status;
      if (processingStatus === 'failed') {
        return 'Failed';
      }
      return 'Completed';
    }
  };

  const isStepCompleted = (step: number) => {
    const processingStatus = status?.processing_status;

    if (step === 0) {
      return processingStatus !== 'pending' && processingStatus !== undefined;
    } else if (step === 1) {
      return processingStatus === 'completed' || processingStatus === 'failed';
    } else {
      return processingStatus === 'completed' || processingStatus === 'failed';
    }
  };

  const isStepActive = (step: number) => {
    const processingStatus = status?.processing_status;

    if (step === 0) {
      return processingStatus === 'pending' || !processingStatus;
    } else if (step === 1) {
      return processingStatus === 'processing';
    } else {
      return false;
    }
  };

  return (
    <Modal
      open={open}
      aria-labelledby='import-status-modal-title'
      aria-describedby='import-status-modal-description'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box className={classes.header}>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            Import Status
          </Typography>
          <CancelIcon
            cursor='pointer'
            width={24}
            height={24}
            onClick={onClose}
          />
        </Box>
        <Box className={classes.content}>
          {/* Pending Step */}
          <Box className={classes.statusItem}>
            <Box className={classes.statusIcon}>{getStatusIcon(0)}</Box>
            <Typography
              className={textClasses.smRegular}
              style={{
                color: isStepActive(0)
                  ? '#5EA1F2'
                  : isStepCompleted(0)
                  ? '#17B26A'
                  : '#667085',
              }}
            >
              {getStatusText(0)}
            </Typography>
          </Box>

          {/* Processing Step */}
          <Box className={classes.statusItem}>
            <Box className={classes.statusIcon}>{getStatusIcon(1)}</Box>
            <Typography
              className={textClasses.smRegular}
              style={{
                color: isStepActive(1)
                  ? '#5EA1F2'
                  : isStepCompleted(1)
                  ? '#17B26A'
                  : '#667085',
              }}
            >
              {getStatusText(1)}
            </Typography>
          </Box>

          {/* Completed/Failed Step */}
          <Box className={classes.statusItem}>
            <Box className={classes.statusIcon}>{getStatusIcon(2)}</Box>
            <Typography
              className={textClasses.smRegular}
              style={{
                color:
                  status?.processing_status === 'failed'
                    ? '#B42318'
                    : isStepCompleted(2)
                    ? '#17B26A'
                    : '#667085',
              }}
            >
              {getStatusText(2)}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.buttons}>
          <Button
            title='Confirm'
            height={46}
            onClick={onClose}
            disabled={loading}
          />
        </Box>
      </Box>
    </Modal>
  );
};
